import { useLayoutEffect, useRef, type RefObject } from 'react';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  type ImperativePanelHandle,
} from 'react-resizable-panels';
import { cn } from '@/shared/cn';
import { AI_RAIL_OPEN_KEY, useChat } from '@/versions/v1/shared/ChatContext';

const RAIL_DEFAULT_OPEN_SIZE = 25;

type AppShellPanelsProps = {
  showRail: boolean;
  main: React.ReactNode;
  rail: React.ReactNode;
  /** Right-edge vertical review chrome — inside the main panel, left of the AI rail. */
  chrome?: React.ReactNode;
};

function MainWithChrome({
  main,
  chrome,
}: {
  main: React.ReactNode;
  chrome?: React.ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 w-full flex-1">
      <div id="scene" className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {main}
      </div>
      {chrome}
    </div>
  );
}

function readInitialRailOpen(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(AI_RAIL_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

export function AppShellPanels({ showRail, main, rail, chrome }: AppShellPanelsProps) {
  const { railPanelRef, openRail, closeRail } = useChat();
  const initialRailOpenRef = useRef(readInitialRailOpen());
  const suppressInitialCollapseRef = useRef(initialRailOpenRef.current);

  useLayoutEffect(() => {
    if (!showRail) return;
    if (!initialRailOpenRef.current) return;
    openRail();
    const id = window.requestAnimationFrame(() => {
      suppressInitialCollapseRef.current = false;
    });
    return () => window.cancelAnimationFrame(id);
  }, [showRail, openRail]);

  if (!showRail) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <MainWithChrome main={main} chrome={chrome} />
      </div>
    );
  }

  return (
    <PanelGroup
      autoSaveId="mock-ui:app-shell"
      direction="horizontal"
      className="flex min-h-0 min-w-0 flex-1"
    >
      <Panel
        id="main"
        defaultSize={75}
        minSize={35}
        className="relative flex min-h-0 min-w-0 flex-col overflow-hidden"
      >
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          <MainWithChrome main={main} chrome={chrome} />
        </div>
      </Panel>

      <RailResizeHandle />

      <Panel
        id="rail"
        ref={railPanelRef as RefObject<ImperativePanelHandle>}
        defaultSize={initialRailOpenRef.current ? RAIL_DEFAULT_OPEN_SIZE : 0}
        minSize={10}
        collapsible
        collapsedSize={0}
        onCollapse={() => {
          if (suppressInitialCollapseRef.current) return;
          closeRail();
        }}
        onExpand={() => {
          suppressInitialCollapseRef.current = false;
          openRail();
        }}
        className="flex min-h-0 min-w-0 flex-col"
      >
        {rail}
      </Panel>
    </PanelGroup>
  );
}

function RailResizeHandle() {
  const { railOpen } = useChat();
  return (
    <PanelResizeHandle
      id="main-rail-divider"
      className={cn(
        'group relative outline-none data-[panel-group-direction=horizontal]:flex',
        railOpen ? 'w-px bg-[var(--glass-border)]' : 'pointer-events-none w-0',
      )}
    >
      {railOpen ? (
        <div className="pointer-events-none absolute inset-y-0 -inset-x-1.5 cursor-col-resize transition-colors group-hover:bg-zinc-300/60 dark:group-hover:bg-zinc-700/40" />
      ) : null}
    </PanelResizeHandle>
  );
}
